import { isGeminiReadableMimeType } from './assignmentAiMimeTypes';
import {
  findLegacyOfficeFormatInAttachments,
  type LegacyOfficeFormat,
  resolveAssignmentAiMimeTypeFromFileName,
} from './resolveAssignmentAiMimeType';

export type PreSubmissionMiaAvailabilityStatus = 'available' | 'warning' | 'disabled';

export type PreSubmissionMiaAvailabilityReason =
  | 'no_attachments'
  | 'unsupported_format'
  | 'student_document_not_read'
  | 'student_legacy_doc_format'
  | 'student_legacy_xls_format'
  | 'instruction_legacy_doc_format'
  | 'instruction_legacy_xls_format';

export type PreSubmissionMiaAvailabilityResult = {
  status: PreSubmissionMiaAvailabilityStatus;
  reason?: PreSubmissionMiaAvailabilityReason;
};

const DISABLED_MIME_PREFIXES = ['audio/', 'video/'] as const;

const DISABLED_MIME_TYPES = new Set([
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

type AttachmentRef = {
  fileName?: string | null;
  mimeType?: string | null;
};

type AttachmentReadiness = 'unsupported' | 'readable' | 'unknown';

/**
 * Classifies an attachment MIME type as supported, unsupported, or unknown for M.ia review.
 * @param mimeType Stored MIME type from the upload metadata.
 * @param fileName File name used as a fallback when MIME type is missing or generic.
 * @returns Readiness classification for pre-submission review.
 */
function classifyAttachmentMime(
  mimeType?: string | null,
  fileName?: string | null,
): AttachmentReadiness {
  const effectiveMimeType = fileName
    ? resolveAssignmentAiMimeTypeFromFileName(fileName, mimeType ?? undefined)
    : (mimeType ?? undefined);

  if (!effectiveMimeType) {
    return 'unknown';
  }

  const normalized = effectiveMimeType.toLowerCase();

  if (DISABLED_MIME_PREFIXES.some((prefix) => normalized.startsWith(prefix))) {
    return 'unsupported';
  }

  if (DISABLED_MIME_TYPES.has(normalized)) {
    return 'unsupported';
  }

  if (isGeminiReadableMimeType(normalized)) {
    return 'readable';
  }

  return 'unknown';
}

/**
 * Aggregates readiness flags across all student deposit attachments.
 * @param attachments Student deposit attachments to evaluate.
 * @returns Flags indicating unsupported, readable, or unknown attachments.
 */
function aggregateReadiness(attachments: AttachmentRef[]): {
  hasUnsupported: boolean;
  hasReadable: boolean;
  hasUnknown: boolean;
} {
  const readiness = attachments.map((attachment) =>
    classifyAttachmentMime(attachment.mimeType, attachment.fileName),
  );

  return {
    hasUnsupported: readiness.some((item) => item === 'unsupported'),
    hasReadable: readiness.some((item) => item === 'readable'),
    hasUnknown: readiness.some((item) => item === 'unknown'),
  };
}

/**
 * Maps a legacy Office format on a student deposit to the corresponding availability reason.
 * @param legacyFormat Detected legacy format from file metadata.
 * @returns Disabled-state reason for pre-submission review.
 */
function toStudentLegacyFormatReason(
  legacyFormat: LegacyOfficeFormat,
): PreSubmissionMiaAvailabilityReason {
  return legacyFormat === 'doc' ? 'student_legacy_doc_format' : 'student_legacy_xls_format';
}

/**
 * Maps a legacy Office format on an instruction document to the corresponding availability reason.
 * @param legacyFormat Detected legacy format from file metadata.
 * @returns Disabled-state reason for pre-submission review.
 */
function toInstructionLegacyFormatReason(
  legacyFormat: LegacyOfficeFormat,
): PreSubmissionMiaAvailabilityReason {
  return legacyFormat === 'doc' ? 'instruction_legacy_doc_format' : 'instruction_legacy_xls_format';
}

/**
 * Determines whether pre-submission M.ia review is available for the current deposit.
 * @param attachments Student deposit attachments to analyze.
 * @param assignmentDocuments Instructor instruction documents attached to the assignment.
 * @returns Availability status and optional reason when disabled or in warning state.
 */
export function getPreSubmissionMiaAvailabilityStatus(
  attachments: AttachmentRef[] = [],
  assignmentDocuments: AttachmentRef[] = [],
): PreSubmissionMiaAvailabilityResult {
  if (!attachments.length) {
    return { status: 'disabled', reason: 'no_attachments' };
  }

  const studentLegacyFormat = findLegacyOfficeFormatInAttachments(attachments);

  if (studentLegacyFormat) {
    return {
      status: 'disabled',
      reason: toStudentLegacyFormatReason(studentLegacyFormat.format),
    };
  }

  const instructionLegacyFormat = findLegacyOfficeFormatInAttachments(assignmentDocuments);

  if (instructionLegacyFormat) {
    return {
      status: 'disabled',
      reason: toInstructionLegacyFormatReason(instructionLegacyFormat.format),
    };
  }

  const { hasUnsupported, hasReadable, hasUnknown } = aggregateReadiness(attachments);

  if (hasUnsupported) {
    return { status: 'disabled', reason: 'unsupported_format' };
  }

  // Warn when every attachment MIME type is unknown and none are confidently readable.
  if (hasUnknown && !hasReadable) {
    return { status: 'warning', reason: 'student_document_not_read' };
  }

  return { status: 'available' };
}
